import React, { useState, useEffect } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const SearchInInputField = ({
  placeholder = "Type to search...",
  fetchOptions,
  onSelect,
  value = "",
  getOptionLabel,
  disabled = false,
  renderOption,
  minSearchLength = 2,
}) => {
  const [searchText, setSearchText] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (searchText.length >= minSearchLength) {
      setLoading(true);
      setError("");
      fetchOptions(searchText)
        .then((res) => {
          setOptions(res || []);
        })
        .catch(() => {
          setOptions([]);
          setError("Failed to fetch results");
        })
        .finally(() => setLoading(false));
    } else {
      setOptions([]);
    }
  }, [searchText, fetchOptions, minSearchLength]);

  const handleSelect = (option) => {
    setSearchText(
      getOptionLabel
        ? getOptionLabel(option)
        : option.label || option.name || ""
    );
    setOpen(false);
    onSelect && onSelect(option);
  };

  return (
    <Box position="relative">
      <TextField
        placeholder={placeholder}
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
          setOpen(true);
        }}
        fullWidth
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          autoComplete: "on",
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && searchText.length >= minSearchLength && (
        <Box
          sx={{
            position: "absolute",
            zIndex: 10,
            width: "100%",
            bgcolor: "background.paper",
            boxShadow: 3,
            borderRadius: 2,
            mt: 0.5,
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          {loading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={2}
            >
              <CircularProgress size={20} />
            </Box>
          ) : error ? (
            <Typography color="error" p={2}>
              {error}
            </Typography>
          ) : options.length === 0 ? (
            <Typography p={2}>No results found</Typography>
          ) : (
            <List dense>
              {options.map((option, idx) => (
                <ListItem key={option._id || option.id || idx} disablePadding>
                  <ListItemButton onClick={() => handleSelect(option)}>
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <ListItemText
                        primary={
                          getOptionLabel
                            ? getOptionLabel(option)
                            : option.label || option.name || ""
                        }
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchInInputField;
